import {
  readLocalSQLQueryOverride,
  writeLocalSQLQueryOverride,
} from 'insights/analytics-queries/data/LocalSQLQueryOverride';
import {AnalyticsQuerySpecResponse} from 'insights/analytics-queries/RESTAnalyticsQueryTypes';
import escapeRegExp from 'util/escapeRegExp';

import {AnalyticsParameterConfigGraphQL} from '../../../../graphql-fragments/insights/__generated/AnalyticsParameterConfigGraphQL';

interface RenameInfo {
  oldName: string;
  newName: string;
}

/**
 * Diff old and new configs, and find which parameters were renamed. Return as an array of (old, new name)
 * objects or RenameInfos.
 */
export function getRenameInfos(
  oldConfigs: Array<AnalyticsParameterConfigGraphQL>,
  newConfigs: Array<AnalyticsParameterConfigGraphQL>
): Array<RenameInfo> {
  const renameInfos: Array<RenameInfo> = [];
  newConfigs.forEach((newConfig) => {
    const matchingOldConfig = oldConfigs.find((oldConfig) => oldConfig.id === newConfig.id);
    if (matchingOldConfig != null) {
      const oldName = matchingOldConfig.systemName;
      const newName = newConfig.systemName;
      if (oldName !== newName) {
        renameInfos.push({oldName, newName});
      }
    }
  });
  return renameInfos;
}

/**
 * Rename all occurrences of parameter in SQL query
 *   - Before: SELECT {{old_name}}
 *   - After: SELECT {{new_name}}
 */
function renameParameterInSQLQuery(sqlQuery: string, oldName: string, newName: string): string {
  // Escape all characters to ensure the old name having special characters doesn't create unexpected behavior
  const oldTagRegex = escapeRegExp(`{{${oldName}}}`);
  const newTag = `{{${newName}}}`;
  return sqlQuery.replace(new RegExp(oldTagRegex, 'g'), newTag);
}

/**
 * Apply all renames to SQL query
 */
export function getRenamedSQLQuery(oldSQLQuery: string, renameInfos: Array<RenameInfo>): string {
  let newSQLQuery = oldSQLQuery;
  renameInfos.forEach(({oldName, newName}) => {
    newSQLQuery = renameParameterInSQLQuery(newSQLQuery, oldName, newName);
  });
  return newSQLQuery;
}

/**
 * Apply all renames to local SQL queries given the changes detected between old and new configs
 */
export function renameParametersInLocalSQLQueryOverrides(
  analyticsQueries: Array<Pick<AnalyticsQuerySpecResponse, 'id'>>,
  oldConfigs: Array<AnalyticsParameterConfigGraphQL>,
  newConfigs: Array<AnalyticsParameterConfigGraphQL>
): void {
  const renameInfos = getRenameInfos(oldConfigs, newConfigs);

  analyticsQueries.forEach(({id: analyticsQueryId}) => {
    const localSQLQueryOverride = readLocalSQLQueryOverride(analyticsQueryId);
    if (localSQLQueryOverride != null) {
      writeLocalSQLQueryOverride(analyticsQueryId, getRenamedSQLQuery(localSQLQueryOverride, renameInfos));
    }
  });
}
